import type { FormScope, FormVersion } from "../state/formVersion";
import { useFormVersionState } from "../state/formVersion";
import { groupBy, isEmpty, isFunction, isNil, uniq, uniqBy } from "lodash-es";
import { createEffect, on } from "solid-js";
import { trackStore } from "@solid-primitives/deep";
import type { SetStoreFunction } from "solid-js/store";
import { reconcile } from "solid-js/store";
import type { BaseIssue, IssuePathItem } from "valibot";
import { safeParse, safeParseAsync } from "valibot";
import { toField } from "../fields/fields";
import type {
  FieldError,
  IntegrationError,
  Issue,
  IssuePathLocation,
  IssueScope,
  RuleError,
  SubformError,
  Validation,
  VariableError,
} from "../state/validation";
import { useValidationDispatch, useValidationState } from "../state/validation";
import { initialized } from "../utils/initializedUtil";
import type FieldId from "../fields/FieldId";
import type RuleId from "../fields/RuleId";
import { schema } from "../utils/validation/rule";
import SubformId from "../state/SubformId";
import { toIntegration } from "../integrations/integrations";
import type IntegrationId from "../integrations/IntegrationId";
import type { Integration } from "../integrations/types";
import type { BaseField, FieldConfig, ResourceSchema, VariableConfig } from "../fields/types";
import { Scope } from "../types";
import { getDataNameIssues, getVariableNameIssues } from "../utils/validationUtil";
import toVariableDefinition from "../variables/toVariableDefinition";
import type VariableId from "../variables/VariableId";
import { createFieldDataNameMap, createVariableDataNameMap } from "../utils/dataNameUtil";

let setValidationStoreInstance: SetStoreFunction<Validation> | null = null;
let validationStoreInstance: Validation | null = null;
let formVersionStoreInstance: FormVersion | null = null;

const resolveSchema = (schema: ResourceSchema) => (isFunction(schema) ? schema(formVersionStore().formId) : schema);

const combineFieldErrors = (fields: FieldConfig[], errors: FieldError[], asyncErrors: FieldError[]): FieldError[] =>
  // loop through all fields to ensure correct field / error order
  fields
    .map((field) => {
      const error = errors.find((e) => e.fieldId.equals(field.uid));
      const asyncError = asyncErrors.find((e) => e.fieldId.equals(field.uid));

      // no errors
      if (isNil(error) && isNil(asyncError)) {
        return undefined;
      }

      // only synchronous errors
      if (isNil(asyncError)) {
        return error;
      }

      // only asynchronous errors
      if (isNil(error)) {
        return asyncError;
      }

      // give async errors precedence, only keep one error per path
      const issues = uniqBy([...asyncError.issues, ...error.issues], (issue) => JSON.stringify(issue.path));

      return { ...error, issues };
    })
    .filter((e) => !isNil(e));

type ValibotIssue = Pick<BaseIssue<unknown>, "path" | "message">;

const toIssuePathLocation = ({ type, origin, key }: IssuePathItem) => ({ type, origin, key }) as IssuePathLocation;

// valibot's path items also hold the validated objects (`input`, `value`): FormVersion store nodes. Stores share raw
// objects, so storing those lets `reconcile` write into the FormVersion store, which re-runs validation endlessly.
const toIssue = ({ path, message }: ValibotIssue): Issue => {
  if (isNil(path)) {
    return { path, message };
  }

  const [first, ...rest] = path;
  return { path: [toIssuePathLocation(first), ...rest.map(toIssuePathLocation)], message };
};

const mapFieldIssues = (
  issues: ValibotIssue[],
  errors: FieldError[],
  config: FieldConfig,
  fieldType: BaseField<unknown, unknown>,
) => {
  if (isEmpty(issues)) {
    return errors;
  }

  const mappedIssues = issues.map(toIssue);

  return [
    ...errors,
    { fieldId: config.uid, scope: config.scope, fieldType: fieldType.type, issues: mappedIssues },
  ] as FieldError[];
};

const mapVariableIssues = (
  issues: ValibotIssue[],
  errors: VariableError[],
  config: VariableConfig,
): VariableError[] => {
  if (isEmpty(issues)) {
    return errors;
  }

  const mappedIssues = issues.map(toIssue);

  return [...errors, { variableId: config.uid, variableType: config.type, issues: mappedIssues }];
};

const combineIntegrationErrors = (
  integrations: Integration[],
  errors: IntegrationError[],
  asyncErrors: IntegrationError[],
): IntegrationError[] =>
  // loop through all fields to ensure correct field / error order
  integrations
    .map((integration) => {
      const error = errors.find((e) => e.integrationId.equals(integration.id));
      const asyncError = asyncErrors.find((e) => e.integrationId.equals(integration.id));

      // no errors
      if (isNil(error) && isNil(asyncError)) {
        return undefined;
      }

      // only synchronous errors
      if (isNil(asyncError)) {
        return error;
      }

      // only asynchronous errors
      if (isNil(error)) {
        return asyncError;
      }

      // give async errors precedence, only keep one error per path
      const issues = uniqBy([...asyncError.issues, ...error.issues], (issue) => JSON.stringify(issue.path));

      return { ...error, issues };
    })
    .filter((e) => !isNil(e));

const mapIntegrationIssues = (issues: ValibotIssue[], errors: IntegrationError[], config: Integration) => {
  if (isEmpty(issues)) {
    return errors;
  }

  const mappedIssues = issues.map(toIssue);

  return [...errors, { integrationId: config.id, issues: mappedIssues }] as IntegrationError[];
};

const init = () => {
  setValidationStoreInstance = useValidationDispatch();
  validationStoreInstance = useValidationState();
  formVersionStoreInstance = useFormVersionState();

  // Field validation
  createEffect(
    on(
      [() => trackStore(formVersionStore().fields), () => trackStore(formVersionStore().variables)],
      async ([fields, variables]) => {
        const fieldDataNameMap = createFieldDataNameMap(fields);
        const variableDataNameMap = createVariableDataNameMap(variables);

        const errors = fields.reduce<FieldError[]>((acc, curr) => {
          const fieldType = toField(curr.type);
          const issues = safeParse(fieldType.schema, curr.properties).issues ?? [];
          // Check for duplicate dataNames since we cannot check this in validation schemes because they can be duplicated within a scope
          const dataNameIssues = getDataNameIssues(curr, fieldDataNameMap, variableDataNameMap);

          return mapFieldIssues([...dataNameIssues, ...issues], acc, curr, fieldType);
        }, []);

        // Set errors so synchronous errors are already shown while async errors are still processing
        setValidationStore()("fieldErrors", reconcile(errors, { key: "fieldId" }));

        const asyncErrors = await fields
          .filter((field) => !isNil(toField(field.type).resourceSchema))
          .reduce(async (acc, curr) => {
            const fieldType = toField(curr.type);
            const issues =
              (await safeParseAsync(resolveSchema(fieldType.resourceSchema!), curr.properties)).issues ?? [];
            return mapFieldIssues(issues, await acc, curr, fieldType);
          }, Promise.resolve<FieldError[]>([]));

        setValidationStore()(
          "fieldErrors",
          reconcile(combineFieldErrors(fields, errors, asyncErrors), { key: "fieldId" }),
        );
      },
    ),
  );

  // Subform validation
  createEffect(
    on(
      [() => trackStore(validationStore().fieldErrors), () => trackStore(validationStore().ruleErrors)],
      ([fieldErrors, ruleErrors]) => {
        const fieldErrorsPerSubform = groupBy(
          fieldErrors.filter((error) => error.scope !== Scope.ROOT),
          "scope",
        );
        const ruleErrorsPerSubform = groupBy(
          ruleErrors.filter((error) => error.scope !== Scope.ROOT),
          "scope",
        );

        const errors = uniq([...Object.keys(fieldErrorsPerSubform), ...Object.keys(ruleErrorsPerSubform)]).map(
          (subformId) => ({
            subformId: SubformId.from(subformId),
            fieldIds: (fieldErrorsPerSubform[subformId] ?? []).map((e) => e.fieldId),
            ruleIds: (ruleErrorsPerSubform[subformId] ?? []).map((e) => e.ruleId),
          }),
        );

        setValidationStore()("subformErrors", reconcile(errors, { key: "subformId" }));
      },
    ),
  );

  // Rule validation
  createEffect(
    on([() => trackStore(formVersionStore().rules), () => trackStore(formVersionStore().fields)], ([rules]) => {
      const errors = rules.reduce<RuleError[]>((acc, curr) => {
        const issues: Issue[] | undefined = safeParse(schema, curr).issues?.map((issue) => {
          const scope: IssueScope =
            (issue.path as { value?: { scope?: IssueScope } }[])?.find(
              (path) => typeof path.value === "object" && path.value?.scope !== undefined,
            )?.value?.scope ?? "field";
          return { ...toIssue(issue), scope };
        });
        return isEmpty(issues)
          ? acc
          : ([
              ...acc,
              {
                ruleId: curr.id,
                scope: curr.scope,
                label: curr.name,
                issues,
              },
            ] as RuleError[]);
      }, []);

      setValidationStore()("ruleErrors", reconcile(errors, { key: "ruleId" }));
    }),
  );

  // Integration validation
  createEffect(
    on(
      [() => trackStore(formVersionStore().integrations), () => trackStore(formVersionStore().fields)],
      async ([integrations]) => {
        const errors = integrations.reduce<IntegrationError[]>((acc, curr) => {
          const definition = toIntegration(curr.type);
          const issues = safeParse(definition.schema, curr.configuration).issues ?? [];
          return mapIntegrationIssues(issues, acc, curr);
        }, []);

        // Set errors so synchronous errors are already shown while async errors are still processing
        setValidationStore()("integrationErrors", reconcile(errors, { key: "integrationId" }));

        const asyncErrors = await integrations
          .filter((integration) => !isNil(toIntegration(integration.type).resourceSchema))
          .reduce(async (acc, curr) => {
            const definition = toIntegration(curr.type);
            const issues =
              (await safeParseAsync(resolveSchema(definition.resourceSchema!), curr.configuration)).issues ?? [];
            return mapIntegrationIssues(issues, await acc, curr);
          }, Promise.resolve<IntegrationError[]>([]));

        setValidationStore()(
          "integrationErrors",
          reconcile(combineIntegrationErrors(integrations as Integration[], errors, asyncErrors), {
            key: "integrationId",
          }),
        );
      },
    ),
  );

  // Variables validation
  createEffect(
    on(
      [() => trackStore(formVersionStore().variables), () => trackStore(formVersionStore().fields)],
      ([variables, fields]) => {
        const fieldDataNameMap = createFieldDataNameMap(fields);
        const variableDataNameMap = createVariableDataNameMap(variables);

        const errors = variables.reduce<VariableError[]>((acc, curr) => {
          const variableDefinition = toVariableDefinition(curr.type);
          const issues = safeParse(variableDefinition.schema, curr).issues ?? [];
          const dataNameIssues = getDataNameIssues(curr, fieldDataNameMap, variableDataNameMap);
          const nameIssues = getVariableNameIssues(curr, variables);

          return mapVariableIssues([...nameIssues, ...dataNameIssues, ...issues], acc, curr);
        }, []);

        setValidationStore()("variableErrors", reconcile(errors, { key: "variableId" }));
      },
    ),
  );
};

const validationStore = () => initialized(validationStoreInstance, "validationStore");
const setValidationStore = () => initialized(setValidationStoreInstance, "setValidationStore");
const formVersionStore = () => initialized(formVersionStoreInstance, "formVersionStore");

export type ValidationServiceType = {
  getFieldErrors: () => FieldError[];
  getVariableErrors: () => VariableError[];
  getSubformErrors: () => SubformError[];
  getRuleErrors: () => RuleError[];
  getIntegrationErrors: () => IntegrationError[];

  getIssuesForField: (fieldId: FieldId) => Issue[];
  getIssuesForVariable: (variableId: VariableId) => Issue[];
  getIssuesForRule: (ruleId: RuleId) => Issue[];
  getIssuesForIntegration: (integrationId: IntegrationId) => Issue[];

  hasFieldErrors: () => boolean;
  hasVariableErrors: () => boolean;
  hasSubformErrors: () => boolean;
  hasRuleErrors: () => boolean;
  hasIntegrationErrors: () => boolean;
  hasErrors: () => boolean;
};

const create = (scopingFilter: (scope: FormScope) => boolean, subformId?: SubformId): ValidationServiceType => {
  const getFieldErrors = () => validationStore().fieldErrors.filter((error) => scopingFilter(error.scope));
  const getVariableErrors = () => (isNil(subformId) ? validationStore().variableErrors : []);
  const getSubformErrors = () => (isNil(subformId) ? validationStore().subformErrors : []);
  const getRuleErrors = () => validationStore().ruleErrors.filter((error) => scopingFilter(error.scope));
  const getIntegrationErrors = () => (isNil(subformId) ? validationStore().integrationErrors : []);

  const getIssuesForField = (fieldId: FieldId) =>
    validationStore().fieldErrors.find((error) => fieldId.equals(error.fieldId))?.issues ?? [];
  const getIssuesForVariable = (variableId: VariableId) =>
    validationStore().variableErrors.find((error) => variableId.equals(error.variableId))?.issues ?? [];
  const getIssuesForRule = (ruleId: RuleId) =>
    validationStore().ruleErrors.find((error) => ruleId.equals(error.ruleId))?.issues ?? [];
  const getIssuesForIntegration = (integrationId: IntegrationId) =>
    validationStore().integrationErrors.find((error) => integrationId.equals(error.integrationId))?.issues ?? [];

  const hasFieldErrors = () => !isEmpty(getFieldErrors());
  const hasVariableErrors = () => !isEmpty(getVariableErrors());
  const hasSubformErrors = () => !isEmpty(getSubformErrors());
  const hasRuleErrors = () => !isEmpty(getRuleErrors());
  const hasIntegrationErrors = () => !isEmpty(getIntegrationErrors());

  return {
    getFieldErrors,
    getVariableErrors,
    getSubformErrors,
    getRuleErrors,
    getIntegrationErrors,

    getIssuesForField,
    getIssuesForVariable,
    getIssuesForRule,
    getIssuesForIntegration,

    hasFieldErrors,
    hasVariableErrors,
    hasSubformErrors,
    hasRuleErrors,
    hasIntegrationErrors,

    hasErrors: () =>
      hasFieldErrors() || hasVariableErrors() || hasSubformErrors() || hasRuleErrors() || hasIntegrationErrors(),
  };
};

export default {
  init,
  create,
};
